import { useContext } from "react";
import { ServicesContext } from "./ServicesContext";
import { Services } from "./Services";

/**
 * Hook to access services from the ServicesContext.
 *
 * Must be used within a component tree wrapped by ServicesProvider.
 * Throws an error if services are not available in the context.
 *
 * @returns Services object containing all available services
 * @throws Error if used outside of ServicesProvider
 *
 * @example
 * ```tsx
 * // Destructure specific services you need
 * function MyComponent() {
 *   const { api, logger, queryBuilderFactory } = useServices();
 *
 *   const loadData = async () => {
 *     try {
 *       const filters = queryBuilderFactory
 *         .create()
 *         .withFilters({ name: { eq: 'test' } })
 *         .build();
 *       const result = await api.request(
 *         { path: '/items', query: filters }
 *       );
 *       return result;
 *     } catch (error) {
 *       logger.captureException(error, { context: 'loadData' });
 *       throw error;
 *     }
 *   };
 * }
 * ```
 */
export function useServices(): Services {
  const services = useContext(ServicesContext);

  if (!services) {
    throw new Error(
      "useServices must be used within a component wrapped by ServicesProvider. " +
        "Make sure ServicesProvider is at the root of your application.",
    );
  }

  return services;
}
